import React, { useState, useEffect } from 'react';

// LocationAddModal component for adding or editing location details in a modal
const LocationAddModal = ({
  show, // Boolean to control modal visibility
  onClose, // Function to close the modal
  formData, // Object containing form input data
  onChange, // Function to handle form input changes
  onSave, // Function to save form data
  isEditMode, // Boolean to determine if modal is in edit mode
  isLoading, // Boolean to indicate loading state during save
}) => {
  // State to track whether form fields have been touched for validation
  const [touchedFields, setTouchedFields] = useState({
    location_name: false,
    longitude: false,
    latitude: false,
  });

  // State for form validation errors
  const [errors, setErrors] = useState({
    location_name: '',
    longitude: '',
    latitude: '',
  });

  // Reset touchedFields and errors when modal is reopened
  useEffect(() => {
    if (show) {
      setTouchedFields({
        location_name: false,
        longitude: false,
        latitude: false,
      });
      setErrors({
        location_name: '',
        longitude: '',
        latitude: '',
      });
    }
  }, [show]); // Dependency to reset when modal visibility changes

  /**
   * Handles form input changes with validation for coordinate fields
   * @param {Object} e - The event object
   */
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Validation for coordinate fields
    if (name === 'longitude' || name === 'latitude') {
      // Allow numbers, decimal point, and negative sign (valid coordinate format)
      if (/^-?\d*\.?\d*$/.test(value) || value === '') {
        onChange(e); // Update formData only if valid
        setErrors((prev) => ({ ...prev, [name]: '' }));
      } else {
        setErrors((prev) => ({
          ...prev,
          [name]: `${name.charAt(0).toUpperCase() + name.slice(1)} must contain only numbers, a decimal point, or a negative sign`,
        }));
        return;
      }
    } else {
      // For location_name, pass through without validation
      onChange(e);
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  /**
   * Handles form submission
   * @param {Object} e - The event object
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    // Mark all fields as touched when submitting
    setTouchedFields({
      location_name: true,
      longitude: true,
      latitude: true,
    });

    // Validate form fields
    const newErrors = {};
    let isValid = true;

    if (!formData.location_name?.trim()) {
      newErrors.location_name = 'Location name is required';
      isValid = false;
    }
    if (!formData.latitude?.trim()) {
      newErrors.latitude = 'Latitude is required';
      isValid = false;
    } else if (!/^-?\d*\.?\d*$/.test(formData.latitude)) {
      newErrors.latitude = 'Latitude must contain only numbers, a decimal point, or a negative sign';
      isValid = false;
    }
    if (!formData.longitude?.trim()) {
      newErrors.longitude = 'Longitude is required';
      isValid = false;
    } else if (!/^-?\d*\.?\d*$/.test(formData.longitude)) {
      newErrors.longitude = 'Longitude must contain only numbers, a decimal point, or a negative sign';
      isValid = false;
    }

    setErrors(newErrors);

    // Proceed with save if all fields are valid
    if (isValid) {
      onSave();
    }
  };

  /**
   * Marks a field as touched when it loses focus
   * @param {string} fieldName - The name of the field
   */
  const handleBlur = (fieldName) => {
    if (!touchedFields[fieldName]) {
      setTouchedFields((prev) => ({
        ...prev,
        [fieldName]: true,
      }));
    }
  };

  /**
   * Determines if validation message should be shown for a field
   * @param {string} fieldName - The name of the field to check
   * @returns {boolean} True if validation should be shown
   */
  const shouldShowValidation = (fieldName) => {
    return touchedFields[fieldName] && (errors[fieldName] || !formData[fieldName]?.trim());
  };

  // Render nothing if modal is not shown
  if (!show) return null;

  // Render modal
  return (
    <>
      {/* Modal Overlay */}
      <div
        className="modal-backdrop show"
        style={{
          zIndex: 1050,
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }}
      ></div>

      {/* Modal Dialog */}
      <div
        className="modal d-block"
        tabIndex="-1"
        style={{ zIndex: 1060 }}
        role="dialog"
      >
        <div className="modal-dialog modal-dialog-scrollable modal-lg" role="document">
          <div className="modal-content">
            {/* Header */}
            <div className="modal-header">
              <h5 className="modal-title">
                {isEditMode ? 'Edit Location' : 'Add Location'}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
              />
            </div>

            {/* Body */}
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                {/* Location Name Field */}
                <div className="mb-3">
                  <label htmlFor="locationName" className="form-label fw-semibold">
                    Location Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="locationName"
                    name="location_name"
                    className={`form-control ${shouldShowValidation('location_name') ? 'is-invalid' : ''}`}
                    placeholder="Enter location name"
                    value={formData.location_name || ''}
                    onChange={handleChange}
                    onBlur={() => handleBlur('location_name')}
                    required
                  />
                  {shouldShowValidation('location_name') && (
                    <div className="invalid-feedback">{errors.location_name || 'Location name is required'}</div>
                  )}
                </div>

                {/* Latitude Field */}
                <div className="mb-3">
                  <label htmlFor="latitude" className="form-label fw-semibold">
                    Latitude <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="latitude"
                    name="latitude"
                    className={`form-control ${shouldShowValidation('latitude') ? 'is-invalid' : ''}`}
                    placeholder="Enter latitude (e.g., 12.345 or -12.345)"
                    value={formData.latitude || ''}
                    onChange={handleChange}
                    onBlur={() => handleBlur('latitude')}
                    required
                  />
                  <small className="form-text text-muted">
                    Only numbers, a decimal point, and a negative sign are allowed.
                  </small>
                  {shouldShowValidation('latitude') && (
                    <div className="invalid-feedback">{errors.latitude || 'Latitude is required'}</div>
                  )}
                </div>

                {/* Longitude Field */}
                <div className="mb-3">
                  <label htmlFor="longitude" className="form-label fw-semibold">
                    Longitude <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="longitude"
                    name="longitude"
                    className={`form-control ${shouldShowValidation('longitude') ? 'is-invalid' : ''}`}
                    placeholder="Enter longitude (e.g., 12.345 or -12.345)"
                    value={formData.longitude || ''}
                    onChange={handleChange}
                    onBlur={() => handleBlur('longitude')}
                    required
                  />
                  <small className="form-text text-muted">
                    Only numbers, a decimal point, and a negative sign are allowed.
                  </small>
                  {shouldShowValidation('longitude') && (
                    <div className="invalid-feedback">{errors.longitude || 'Longitude is required'}</div>
                  )}
                </div>
              </form>
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading
                  ? isEditMode
                    ? 'Updating...'
                    : 'Saving...'
                  : isEditMode
                  ? 'Update Location'
                  : 'Save Location'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Export the component
export default LocationAddModal;